/**
 * Barra de búsqueda: texto libre, filtros de un clic y agrupación.
 *
 * La comparten la lista y el kanban para que el criterio de búsqueda sea el
 * mismo en las dos: cambiar de vista no puede cambiar qué registros se ven.
 *
 * Semántica de los filtros: los de un mismo `group` se combinan con **OR** entre
 * sí (son alternativas: "clientes o proveedores"), los grupos distintos con
 * **AND**, y el conjunto se combina con AND con el texto buscado. Casi todos los
 * modelos declaran un solo grupo —el vacío— y ahí es el OR de siempre. El
 * segundo grupo aparece cuando un filtro no es una alternativa sino un alcance
 * ("Todos los consorcios, no sólo el activo"): sumarlo con OR devolvía TODO en
 * vez de acotar. Se dice en pantalla —con un separador «y» y un texto de
 * ayuda—, porque un filtrado que el usuario no entiende es un filtrado en el
 * que no confía.
 */

import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';

import { IconClose, IconSearch } from '@/components/shell/icons';
import type { Domain, ModelSpec } from '@/lib/types';
import { andDomains, filtersDomain, textSearchDomain } from '@/views/view-utils';

const ESPERA_TIPEO = 250;

/**
 * Cómo se lee un corte temporal en el desplegable «Agrupar por».
 *
 * Una agrupación se declara como `campo` o como `campo:granularidad`
 * (`start_date:month`). El rótulo salía del campo y nada más, así que en cuanto
 * un modelo declaró el mes Y el año del mismo campo, el desplegable ofreció dos
 * opciones llamadas las dos «Vendido el» y no había forma de saber cuál era
 * cuál. Con la granularidad adelante se leen solas.
 *
 * Va adelante y no atrás —«Mes de Vendido el», no «Vendido el por mes»— porque
 * lo que el usuario está eligiendo es EL CORTE; el campo es de dónde sale la
 * fecha. Alguien que quiere ver el año busca «Año» en la lista.
 */
const CORTE_TEMPORAL: Record<string, string> = {
  day: 'Día',
  week: 'Semana',
  month: 'Mes',
  quarter: 'Trimestre',
  year: 'Año',
};

function etiquetaDeAgrupacion(spec: ModelSpec, expresion: string): string {
  const [campo = expresion, granularidad] = expresion.split(':');
  const delCampo = spec.fields[campo]?.label ?? campo;
  const corte = granularidad === undefined ? undefined : CORTE_TEMPORAL[granularidad];
  // Una granularidad que no conocemos se muestra tal cual en vez de perderse:
  // si algún día el backend agrega una, acá se ve que existe y no desaparece.
  if (granularidad !== undefined && corte === undefined) return `${delCampo} (${granularidad})`;
  return corte === undefined ? delCampo : `${corte} de ${delCampo}`;
}

export interface SearchState {
  texto: string;
  setTexto: (texto: string) => void;
  filtros: Set<string>;
  alternarFiltro: (nombre: string) => void;
  groupBy: string | null;
  setGroupBy: (campo: string | null) => void;
  limpiar: () => void;
  /** `true` si hay algún criterio puesto por el usuario. */
  hayCriterios: boolean;
  /** Dominio resultante, sin el dominio base de la acción. */
  domain: Domain;
}

export function useSearchState(spec: ModelSpec): SearchState {
  const [texto, setTexto] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filtros, setFiltros] = useState<Set<string>>(() => new Set());
  const [groupBy, setGroupBy] = useState<string | null>(null);

  useEffect(() => {
    const temporizador = window.setTimeout(() => {
      setDebounced(texto);
    }, ESPERA_TIPEO);
    return () => {
      window.clearTimeout(temporizador);
    };
  }, [texto]);

  const busqueda = spec.views.search;
  // Memoizadas a propósito: `?? []` devuelve un array nuevo en cada render y eso
  // haría que el dominio cambie de identidad para siempre. Cualquier efecto que
  // dependa del dominio entraría en un bucle de renders.
  const campos = useMemo(() => busqueda?.fields ?? [], [busqueda]);
  const declarados = useMemo(() => busqueda?.filters ?? [], [busqueda]);

  // `campos` y `declarados` salen de la metadata, que solo cambia con el modelo.
  const domain = useMemo(
    () => andDomains(textSearchDomain(debounced, campos), filtersDomain(declarados, filtros)),
    [debounced, filtros, campos, declarados],
  );

  return {
    texto,
    setTexto,
    filtros,
    alternarFiltro: (nombre: string) => {
      setFiltros((actual) => {
        const copia = new Set(actual);
        if (copia.has(nombre)) copia.delete(nombre);
        else copia.add(nombre);
        return copia;
      });
    },
    groupBy,
    setGroupBy,
    limpiar: () => {
      setTexto('');
      setFiltros(new Set());
      setGroupBy(null);
    },
    hayCriterios: texto.trim() !== '' || filtros.size > 0,
    domain,
  };
}

export function SearchPanel({
  spec,
  estado,
  mostrarAgrupacion = true,
  extra,
}: {
  spec: ModelSpec;
  estado: SearchState;
  /** El kanban ya agrupa por su propio campo: no ofrece el selector. */
  mostrarAgrupacion?: boolean;
  /** Controles propios de la vista (alternador de modo, acciones). */
  extra?: ReactNode;
}) {
  const busqueda = spec.views.search;
  const groupbys = busqueda?.groupbys ?? [];
  const buscables = busqueda?.fields ?? [];

  // Los chips se dibujan agrupados: los de un mismo `group` juntos, en el orden
  // en que aparece cada grupo. Sin esto un grupo podria quedar partido en dos
  // tramos y el separador «y» aparecerian dos veces para el mismo corte.
  const filtros = useMemo(() => {
    const declarados = busqueda?.filters ?? [];
    const orden = new Map<string, number>();
    for (const filtro of declarados) {
      const grupo = filtro.group ?? '';
      if (!orden.has(grupo)) orden.set(grupo, orden.size);
    }
    return [...declarados].sort(
      (a, b) => (orden.get(a.group ?? '') ?? 0) - (orden.get(b.group ?? '') ?? 0),
    );
  }, [busqueda]);

  const etiquetaCampos = buscables
    .map((campo) => spec.fields[campo]?.label ?? campo)
    .slice(0, 3)
    .join(', ');


  const hayVariosGrupos = new Set(filtros.map((filtro) => filtro.group ?? '')).size > 1;

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {buscables.length > 0 && (
          <div className="relative min-w-0 flex-1 sm:max-w-md">
            <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-faint">
              <IconSearch className="h-4 w-4" />
            </span>
            <input
              type="search"
              value={estado.texto}
              onChange={(evento) => {
                estado.setTexto(evento.target.value);
              }}
              placeholder={`Buscar por ${etiquetaCampos.toLowerCase()}…`}
              aria-label={`Buscar ${spec.label_plural.toLowerCase()}`}
              className="touch-target block w-full rounded-md border border-line bg-surface pl-9 pr-3 text-sm transition-colors hover:border-line-strong focus:border-accent sm:h-9 sm:min-h-0"
            />
          </div>
        )}

        {mostrarAgrupacion && groupbys.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-fg-muted">
            <span className="hidden sm:inline">Agrupar por</span>
            <select
              value={estado.groupBy ?? ''}
              onChange={(evento) => {
                estado.setGroupBy(evento.target.value === '' ? null : evento.target.value);
              }}
              aria-label="Agrupar por"
              className="touch-target rounded-md border border-line bg-surface px-2 text-sm transition-colors hover:border-line-strong sm:h-9 sm:min-h-0"
            >
              <option value="">Sin agrupar</option>
              {groupbys.map((campo) => (
                <option key={campo} value={campo}>
                  {etiquetaDeAgrupacion(spec, campo)}
                </option>
              ))}
            </select>
          </label>
        )}

        {extra !== undefined && <div className="ml-auto flex items-center gap-2">{extra}</div>}
      </div>

      {filtros.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="sr-only" id="ayuda-filtros">
            {hayVariosGrupos
              ? 'Los filtros de un mismo grupo se combinan con «o»; los de grupos distintos, con «y».'
              : 'Los filtros elegidos se combinan entre sí con «o».'}
          </span>
          {filtros.map((filtro, indice) => {
            const activo = estado.filtros.has(filtro.name);
            // Un separador donde cambia el grupo: sin nada que lo indique, dos
            // chips que se combinan con «y» se ven igual que dos que se combinan
            // con «o», y el resultado sorprende.
            const anterior = filtros[indice - 1];
            const abreGrupo =
              anterior !== undefined && (anterior.group ?? '') !== (filtro.group ?? '');
            return (
              <Fragment key={filtro.name}>
                {abreGrupo && (
                  <span aria-hidden="true" className="mx-0.5 text-[11px] text-fg-faint">
                    y
                  </span>
                )}
                <button
                  type="button"
                  aria-pressed={activo}
                  aria-describedby="ayuda-filtros"
                  onClick={() => {
                    estado.alternarFiltro(filtro.name);
                  }}
                  className={clsx(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    activo
                      ? 'border-accent bg-accent text-accent-contrast'
                      : 'border-line bg-surface text-fg-muted hover:border-line-strong hover:text-fg',
                  )}
                >
                  {filtro.label}
                </button>
              </Fragment>
            );
          })}

          {estado.hayCriterios && (
            <button
              type="button"
              onClick={estado.limpiar}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
            >
              <IconClose className="h-3 w-3" />
              Limpiar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
